/**
 * Hooks for password reset functionality
 */
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@ui/use-toast';
import {
  requestPasswordReset,
  confirmPasswordReset,
  type MessageResult,
  type PasswordResetRequestData,
  type PasswordResetConfirmData,
} from '@lib/api/auth';
import { toProblem } from '@lib/api/base';

/**
 * Hook for requesting password reset
 */
export const usePasswordResetRequest = () => {
  const { toast } = useToast();

  return useMutation<MessageResult, unknown, PasswordResetRequestData>({
    mutationFn: requestPasswordReset,
    onSuccess: ({ message }) => {
      const description =
        message || 'Please check your email for instructions to reset your password.';

      toast({
        title: 'Password Reset Requested',
        description,
      });
    },
    onError: error => {
      const problem = toProblem(error);
      const description =
        problem.detail ||
        problem.title ||
        'There was a problem requesting a password reset. Please try again.';

      toast({
        title: 'Reset Request Failed',
        description,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for confirming password reset
 */
export const usePasswordResetConfirm = () => {
  const { toast } = useToast();

  return useMutation<MessageResult, unknown, PasswordResetConfirmData>({
    mutationFn: confirmPasswordReset,
    onSuccess: ({ message }) => {
      const description =
        message ||
        'Your password has been reset successfully. You can now log in with your new password.';

      toast({
        title: 'Password Reset Successful',
        description,
      });
    },
    onError: error => {
      const problem = toProblem(error);
      const description =
        problem.detail ||
        problem.title ||
        'There was a problem resetting your password. Please try again.';

      toast({
        title: 'Password Reset Failed',
        description,
        variant: 'destructive',
      });
    },
  });
};
